import { BlankModal, ModalBody, ModalHeader, InlineBanner } from '@cad-arc';
import { useCallback, useEffect } from 'react';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { UnitStateData } from '@mark43/cad-api';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useEventPanelViewQuery } from '../event-panel/event-state/use-event-panel-view-query';
import { useTransitionUnitStatusMutation } from '../unit-status/use-transition-unit-status-mutation';
import { useEnRouteStates } from '../common/hooks/use-en-route-states';
import { AssignmentModalDetails } from './components/assignment-modal-details';
import { IncomingAssignmentModalFooter } from './footer/incoming-assignment-modal-footer';
import { IncomingAssignmentModalFooterAed } from './footer/incoming-assignment-modal-footer-aed';
import { useUnitTimersStore } from '../unit-timers/use-unit-timers-store';
import { WorkspacePanelProvider } from '../panels/workspace-panel-provider';
import { useEventQueueWorkspacePanel } from '../common/hooks/use-event-queue-workspace-panel';
import { usePlaySound } from '../sounds/play-sound-provider';
import { colors } from '../colors/colors';

export type IncomingAssignmentModalComponentType = typeof IncomingAssignmentModal;

export function IncomingAssignmentModal(props: {
    eventId: number;
    isIncomingEventAed?: boolean;
}): React.ReactElement {
    const workspacePanel = useEventQueueWorkspacePanel();
    const unitTimerStore = useUnitTimersStore();
    const eventQuery = useEventPanelViewQuery({ eventId: props.eventId });
    const modalControls = useFullscreenModalControls();
    const transitionUnitStatusMutation = useTransitionUnitStatusMutation();
    const { enRouteState, alternateEnRouteState } = useEnRouteStates(props.isIncomingEventAed);
    const {
        firstResponderPlayAlertOnIncomingAssignment,
        firstResponderPlayAlertOnIncomingAedAssignment,
    } = useEnabledFeatures();
    const { playSound, stopSound } = usePlaySound();

    const errorMessage = ((transitionUnitStatusMutation.error as unknown) as any)?.response?.data
        ?.error;

    const headerColorStyle = props.isIncomingEventAed
        ? { backgroundColor: colors.priorityOneColor }
        : undefined;
    const headerTitle = props.isIncomingEventAed
        ? 'AUTO-DISPATCH ASSIGNMENT'
        : 'Incoming Assignment';

    const shouldPlayIncomingMsgSound = props.isIncomingEventAed
        ? firstResponderPlayAlertOnIncomingAedAssignment
        : firstResponderPlayAlertOnIncomingAssignment;

    useEffect(() => {
        if (shouldPlayIncomingMsgSound) {
            unitTimerStore.disablePlaySound(); // or else it would overwrite the incoming window sound
            playSound('first-responder-incoming-msg');
        }
    }, [props.isIncomingEventAed, shouldPlayIncomingMsgSound, unitTimerStore, playSound]);

    const transitionMutation = useCallback(
        (unitStateData?: UnitStateData) => {
            if (unitStateData) {
                transitionUnitStatusMutation
                    .mutateAsync({
                        unitStateData,
                    })
                    .then((transitioned) => {
                        if (transitioned && shouldPlayIncomingMsgSound) {
                            unitTimerStore.enablePlaySound();
                            stopSound('first-responder-incoming-msg');
                        }
                    });
            }
        },
        [shouldPlayIncomingMsgSound, transitionUnitStatusMutation, unitTimerStore, stopSound]
    );

    const alternateEnRouteStateMutation = useCallback(() => {
        transitionMutation(alternateEnRouteState);
    }, [alternateEnRouteState, transitionMutation]);

    const enRouteStateMutation = useCallback(() => {
        transitionMutation(enRouteState);
    }, [enRouteState, transitionMutation]);

    const commonFooterProps = {
        alternateEnRouteStateMutation,
        alternateEnRouteState,
        mutationIsLoading: transitionUnitStatusMutation?.isLoading,
        enRouteStateMutation,
        enRouteState,
        modalControls,
    };

    return (
        <BlankModal
            isOpen
            onClose={() => modalControls.close()}
            size="lg"
            style={{ maxHeight: 535 }}
            scrollBehavior="inside"
            closeOnOverlayClick={false}
            closeOnEsc={false}
            hideCloseButton
        >
            <ModalHeader style={headerColorStyle}>{headerTitle}</ModalHeader>

            <ModalBody>
                {!!errorMessage && <InlineBanner title={errorMessage} status="error" />}
                {!eventQuery.data ? (
                    <LoadingIndicator />
                ) : (
                    <AssignmentModalDetails eventData={eventQuery.data} />
                )}
            </ModalBody>

            {props.isIncomingEventAed ? (
                <WorkspacePanelProvider workspacePanel={workspacePanel}>
                    <IncomingAssignmentModalFooterAed
                        {...commonFooterProps}
                        eventId={props.eventId}
                    />
                </WorkspacePanelProvider>
            ) : (
                <IncomingAssignmentModalFooter {...commonFooterProps} />
            )}
        </BlankModal>
    );
}
